/**
 * useVqeRunOnChange — "Run on change" for the compact Visual Query Editor
 * inside a Query Editor tab: after every committed visual edit (the vqeStore's
 * `commitFeedback` success stream — structural edits AND the star expansion),
 * re-run the statement under the caret so the results grid follows the query
 * the way the Table Explorer does.
 *
 * Guards, all required: the compact popover is OPEN in this tab, the setting
 * is on, and the cursor statement is a SELECT (`mode === 'select'`) — DML is
 * never auto-run, whatever the setting says. Commits are debounced so a
 * multi-step gesture ("+ all columns") runs once. The SQL is re-read from the
 * live buffer at fire time — never from a pre-edit snapshot — but the run is
 * BOUND to the statement that was edited: the caret offset is captured when
 * the commit lands, so moving the caret to another statement during the
 * debounce cannot redirect the auto-run.
 *
 * The visual `select` mode is a RENDER classification, not a safety one: it
 * also covers `SELECT … INTO`, MySQL `INTO OUTFILE` / `DUMPFILE`, locking
 * `FOR UPDATE` forms, data-modifying CTEs and executable-comment payloads,
 * which write outside the result set or take row locks. So at fire time the
 * exact target SQL is re-checked by `autoRunSafety.ts` (head allowlist plus a
 * dialect-aware write-token scan of the code projection) and anything it
 * refuses is not dispatched; the user can still run it with an explicit
 * Execute click (release review 1.5.0, F2 / R1).
 */
import { useEffect, useRef } from 'react';

import type { DatabaseEngineId } from '../../../../core/engines';
import type { SqlMonacoHandle } from '../../common/utils/sqlMonacoTypes';
import { isAutoRunSafe } from './autoRunSafety';

export { isAutoRunSafe } from './autoRunSafety';
import type { RenderMode } from '../../visual-query-editor/types';
import { useVqeStore } from '../../visual-query-editor/store/vqeStore';
import { findSegmentAtOffset, type StatementSegment } from '../utils/statementAtCursor';

/** Shared with `useVqeVisualRunOnChange` — same setting, same debounce. */
export const RUN_DEBOUNCE_MS = 250;

type Params = {
  readonly active: boolean;
  readonly mode: RenderMode | null;
  /** The tab's engine — narrows the safety projection to one dialect; omitted = strictest of all. */
  readonly engineId?: DatabaseEngineId;
  readonly editorRef: React.RefObject<SqlMonacoHandle | null>;
  readonly getStatementSegments: () => { segments: StatementSegment[] } | null;
  readonly executeStatement: (sql: string) => void;
};

/** Segment under `offset`, else the last one that starts before it. */
export function resolveRunTarget(segments: readonly StatementSegment[], offset: number): StatementSegment | null {
  const exact = findSegmentAtOffset([...segments], offset);
  if (exact) return exact;
  let best: StatementSegment | null = null;
  for (const seg of segments) if (seg.trimStart <= offset) best = seg;
  return best;
}

export function useVqeRunOnChange({
  active, mode, engineId, editorRef, getStatementSegments, executeStatement,
}: Params): void {
  const enabled = active && mode === 'select';
  const commitFeedback = useVqeStore((s) => s.commitFeedback);
  const lastHandledRef = useRef(commitFeedback);
  const executeRef = useRef(executeStatement);
  executeRef.current = executeStatement;

  useEffect(() => {
    if (!enabled) {
      // Never replay a commit that landed while disabled.
      lastHandledRef.current = commitFeedback;
      return undefined;
    }
    if (!commitFeedback || commitFeedback === lastHandledRef.current) return undefined;
    lastHandledRef.current = commitFeedback;
    if (commitFeedback.kind !== 'success') return undefined;
    // Bind the run to the statement being edited NOW, not to wherever the
    // caret sits when the debounce fires.
    const committedOffset = editorRef.current?.getCursorOffset() ?? 0;
    const timer = setTimeout(() => {
      const parsed = getStatementSegments();
      if (!editorRef.current || !parsed) return;
      const target = resolveRunTarget(parsed.segments, committedOffset);
      if (!target) return;
      const sql = target.text.trim();
      if (!sql || !isAutoRunSafe(sql, engineId)) return;
      executeRef.current(target.text);
    }, RUN_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [enabled, commitFeedback, engineId, editorRef, getStatementSegments]);
}
